import { LegacyCheckbox } from "@powerledger/ui-component-lib";
import { Field } from "formik";
import { useTranslation } from "react-i18next";
import { Flex, Text } from "theme-ui";

import { TradePosition } from "@/app/types/generated/graphql";

import { AppCleaveTypes, CleaveInput } from "../../cleave-input";
import { Form, FormFieldLabel } from "../../form";
import ForwardTradeOrderSummary from "../order-summary";

export const VolumeAndPriceInputFields = ({
  currencySymbol,
  isPositionBid = true,
}: {
  currencySymbol: string;
  isPositionBid?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Flex
      sx={{
        flexDirection: "row",
        gap: 2,
        justifyContent: "space-between",
      }}
    >
      <Form.Item sx={{ flex: 1 }}>
        <FormFieldLabel small name="volume" label={t("Product Quantity *")}>
          <Field
            name="volume"
            sx={{
              input: { fontSize: 3 },
            }}
            component={CleaveInput}
            type={AppCleaveTypes.Quantity}
            variant="input"
          />
        </FormFieldLabel>
      </Form.Item>
      <Form.Item sx={{ flex: 1 }}>
        <FormFieldLabel small name="unitPrice" label={t(`${isPositionBid ? "Bid" : "Offer"} Price per Unit *`)}>
          <Field
            name="unitPrice"
            prefix={currencySymbol}
            sx={{
              input: { fontSize: 3 },
            }}
            component={CleaveInput}
            type={AppCleaveTypes.Amount}
            variant="input"
          />
        </FormFieldLabel>
      </Form.Item>
    </Flex>
  );
};

export const ForwardSummaryAndAgreement: React.FC<{
  currencySymbol: string;
  isPositionBid: boolean;
  tradePosition: TradePosition;
  unitPrice: number;
  volume: number;
  fee: number;
  disclosureAgreement?: boolean;
  tncAgreed?: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  requireAgreement: boolean;
  disclosureText: string;
  agreementDisclaimerText: string;
  overLimitErrorMessage?: string;
}> = ({
  currencySymbol,
  isPositionBid,
  tradePosition,
  unitPrice,
  volume,
  fee,
  disclosureAgreement,
  tncAgreed,
  children,
  requireAgreement,
  disclosureText,
  agreementDisclaimerText,
  setFieldValue,
  overLimitErrorMessage,
}) => {
  const { t } = useTranslation();
  return (
    <Flex
      sx={{
        flexDirection: "column",
        width: "50%",
        height: "100%",
        backgroundColor: "white",
        padding: 3,
        borderRadius: 4,
        mt: 3,
      }}
    >
      <Flex sx={{ flexDirection: "column" }}>
        <Flex sx={{ flexDirection: "column", fontVariantNumeric: "tabular-nums" }}>
          <ForwardTradeOrderSummary
            currencySymbol={currencySymbol}
            title={`Your ${isPositionBid ? "Bid" : "Offer"} Order`}
            unitPrice={unitPrice}
            volume={volume}
            orderPosition={tradePosition}
            fee={fee}
          />
        </Flex>
        <Flex sx={{ pt: 1 }}>
          <Text
            sx={{
              color: "textDark",
              fontSize: "0.6em",
            }}
          >
            {t("(This amount will be {{debitedCredited}} your Linked Bank Account)", {
              debitedCredited: isPositionBid ? "debited from" : "credited to",
            })}
          </Text>
        </Flex>
        {overLimitErrorMessage && (
          <Flex sx={{ pt: 2 }}>
            <Text sx={{ fontSize: 0, color: "error.500" }}>{t(overLimitErrorMessage)}</Text>
          </Flex>
        )}
        {requireAgreement ? (
          <Flex
            sx={{ mt: 4, flexDirection: "column", fontFamily: "MintGroteskV08", gap: 3 }}
            data-testid={"forward-trade-agreement"}
          >
            <Flex sx={{ flexDirection: "column", gap: 2 }}>
              <Text sx={{ fontWeight: "bold" }}>{t("Forward Trade Agreement")}</Text>
            </Flex>

            <Form.Item
              sx={{
                gap: 4,
                flexDirection: "row",
              }}
            >
              <LegacyCheckbox
                checked={disclosureAgreement}
                onChange={() => {
                  setFieldValue("disclosureAgreement", !disclosureAgreement);
                }}
                id={"disclosure-agreement-checkbox"}
              />
              <Text sx={{ fontSize: 14 }}>
                <Text sx={{ fontSize: 14 }}>{t(disclosureText)}</Text>
              </Text>
            </Form.Item>

            <Form.Item
              sx={{
                gap: 4,
                flexDirection: "row",
              }}
            >
              <LegacyCheckbox
                checked={tncAgreed}
                onChange={() => {
                  setFieldValue("tncAgreed", !tncAgreed);
                }}
                id={"tnc-agreed-checkbox"}
              />
              <Text sx={{ fontSize: 14 }}>
                <Text sx={{ fontSize: 14 }}>{t(agreementDisclaimerText)}</Text>
              </Text>
            </Form.Item>
          </Flex>
        ) : (
          <></>
        )}
      </Flex>

      {children}
    </Flex>
  );
};

export * from "./common.types";
